import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../App';
import { 
  Users, Building2, Crown, Sparkles, Package, 
  TrendingUp, AlertCircle, RefreshCw, Search,
  ChevronDown, ChevronUp, ExternalLink, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedAccount, setExpandedAccount] = useState(null);

  // Check if user is admin (for now, allow specific emails)
  const isAdmin = user?.email === 'pundari@outlook.com' || 
                  user?.email === 'demo-premium@maadabbu.com' ||
                  user?.email === 'admin@maadabbu.com';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchAdminData();
  }, [isAdmin, navigate]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/accounts');
      setStats(response.data.stats);
      setAccounts(response.data.accounts);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts
    .filter(acc => {
      const matchesSearch = 
        acc.family_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.admin_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.admin_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPlan = filterPlan === 'all' || acc.plan === filterPlan;
      return matchesSearch && matchesPlan;
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'holdings' || sortBy === 'family_members' || sortBy === 'portfolio_value') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const getPlanBadge = (plan) => {
    if (plan === 'premium') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full">
          <Crown size={12} />
          Premium
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
        <Sparkles size={12} />
        Free
      </span>
    );
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield className="text-primary" />
              Admin Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Manage and monitor all Maa Dabbu accounts</p>
          </div>
          <Button onClick={fetchAdminData} variant="outline" className="gap-2">
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card bg-gradient-to-br from-primary/10 to-emerald-50">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/20 rounded-xl">
                <Building2 className="text-primary" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Families</p>
                <p className="text-2xl font-bold">{stats?.total_families || 0}</p>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-blue-50 to-indigo-50">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-amber-50 to-orange-50">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Crown className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Premium</p>
                <p className="text-2xl font-bold text-amber-600">{stats?.premium_accounts || 0}</p>
              </div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-gray-50 to-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-xl">
                <Sparkles className="text-gray-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Free</p>
                <p className="text-2xl font-bold">{stats?.free_accounts || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="card text-center">
            <Package className="mx-auto text-primary mb-2" size={28} />
            <p className="text-2xl font-bold">{stats?.total_holdings?.toLocaleString() || 0}</p>
            <p className="text-sm text-gray-500">Total Holdings</p>
          </div>
          <div className="card text-center">
            <TrendingUp className="mx-auto text-green-600 mb-2" size={28} />
            <p className="text-2xl font-bold">{formatCurrency(stats?.total_portfolio_value)}</p>
            <p className="text-sm text-gray-500">Total AUM</p>
          </div>
          <div className="card text-center">
            <Users className="mx-auto text-blue-600 mb-2" size={28} />
            <p className="text-2xl font-bold">{stats?.total_family_members || 0}</p>
            <p className="text-sm text-gray-500">Family Members</p>
          </div>
        </div>

        {/* Accounts Table */}
        <div className="card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <h2 className="text-xl font-semibold">All Accounts</h2>
            
            <div className="flex flex-wrap gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64"
                />
              </div>
              
              {/* Plan Filter */}
              <select
                value={filterPlan}
                onChange={(e) => setFilterPlan(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Plans</option>
                <option value="premium">Premium Only</option>
                <option value="free">Free Only</option>
              </select>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="created_at">Created Date</option>
                <option value="family_name">Family Name</option>
                <option value="holdings">Holdings</option>
                <option value="portfolio_value">Portfolio Value</option>
              </select>
              
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 border rounded-lg hover:bg-gray-50"
              >
                {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4">Family</th>
                  <th className="text-left py-3 px-4">Admin</th>
                  <th className="text-center py-3 px-4">Plan</th>
                  <th className="text-center py-3 px-4">Members</th>
                  <th className="text-center py-3 px-4">Holdings</th>
                  <th className="text-right py-3 px-4">Portfolio Value</th>
                  <th className="text-center py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <React.Fragment key={account.family_id}>
                    <tr 
                      className={`border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                        expandedAccount === account.family_id ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => setExpandedAccount(
                        expandedAccount === account.family_id ? null : account.family_id
                      )}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {expandedAccount === account.family_id ? 
                            <ChevronUp size={16} className="text-gray-400" /> : 
                            <ChevronDown size={16} className="text-gray-400" />
                          }
                          <span className="font-medium">{account.family_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{account.admin_name}</p>
                          <p className="text-xs text-gray-500">{account.admin_email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getPlanBadge(account.plan)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold">{account.family_members}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-semibold">{account.holdings}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-semibold text-primary">
                          {formatCurrency(account.portfolio_value)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Could add "View as" functionality here
                          }}
                        >
                          <ExternalLink size={14} />
                        </Button>
                      </td>
                    </tr>
                    
                    {/* Expanded Details */}
                    {expandedAccount === account.family_id && (
                      <tr>
                        <td colSpan={7} className="bg-gray-50 px-4 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Family ID</p>
                              <p className="font-mono text-xs">{account.family_id}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Created</p>
                              <p className="text-sm">{account.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Total Loans</p>
                              <p className="text-sm font-semibold">{formatCurrency(account.total_loans)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Last Activity</p>
                              <p className="text-sm">{account.last_activity || 'N/A'}</p>
                            </div>
                          </div>
                          
                          {account.members && account.members.length > 0 && (
                            <div className="mt-4">
                              <p className="text-xs text-gray-500 uppercase mb-2">Family Members</p>
                              <div className="flex flex-wrap gap-2">
                                {account.members.map((member, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-white border rounded text-xs">
                                    {member.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAccounts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="mx-auto mb-2" size={32} />
              <p>No accounts found matching your criteria</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
